import { useMutation } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { MUTATION_DUPLICATE_APPLICATION } from "api/mutations/application";
import { applicationStepOne } from "pages/ApplicationForm/formSteps";
import { canContinueApplication } from "pages/ApplicationForm/utils";
import { CaseApplication } from "../types";
import useRecheck from "./useRecheck";

// Re-requesting duplicates the latest submission and opens the copy in the form.
const useReopenApplication = (
  applications: CaseApplication[],
  canSubmitApplication: boolean,
  isBroker: boolean,
  refreshApplications: () => Promise<CaseApplication[]>,
) => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    checking,
    problem,
    isStale,
    recheck,
    reset: resetRecheck,
  } = useRecheck();

  const latestSubmission = applications.find((app) => app.isLatestSubmission);
  const hasOpenApplications = applications.some((app) =>
    canContinueApplication(app.status),
  );
  const canReopen =
    isBroker &&
    canSubmitApplication &&
    !!latestSubmission &&
    !hasOpenApplications;

  const [duplicateApplication, { loading, error, reset }] = useMutation(
    MUTATION_DUPLICATE_APPLICATION,
    {
      onCompleted: (data) =>
        navigate(applicationStepOne(`${data.data.application.uuid}`)),
    },
  );

  // Someone else has re-requested if there's now a draft, or a newer submission.
  const isUnchanged = async () => {
    const latest = await refreshApplications();
    return (
      !latest.some((app) => canContinueApplication(app.status)) &&
      latest.find((app) => app.isLatestSubmission)?.uuid ===
        latestSubmission?.uuid
    );
  };

  const onConfirm = async () => {
    if (
      !latestSubmission ||
      !(await recheck(
        isUnchanged,
        "This application has already been re-requested.",
      ))
    ) {
      return;
    }
    duplicateApplication({
      variables: { applicationId: latestSubmission.uuid },
    }).catch(() => {});
  };

  return {
    canReopen,
    openDialog: () => setIsDialogOpen(true),
    dialogProps: {
      open: isDialogOpen,
      onClose: () => setIsDialogOpen(false),
      afterLeave: () => {
        reset();
        resetRecheck();
      },
      onConfirm,
      loading: checking || loading,
      error: problem || !!error,
      confirmDisabled: isStale,
    },
  };
};

export default useReopenApplication;
